import { Button, Typography } from '@mui/material'
import CoreModal from 'core/CoreModal'
import ModalInfoIcon from 'assets/icons-color/modal-info.png'
import { Box } from '@mui/system'
import { GitBook } from 'constants/externalLink'

export type infoModalKind =
  | 'Borrowing power'
  | 'Collateral factor'
  | 'Borrowing factor'
  | 'Borrow limit'
  | 'Utilization rate'
  | 'Risk Ratio'
  | 'Boost borrowing power with collateral'
  | 'Liquidation incentive'
  | 'Reserve factor'

interface Props {
  onClose: () => void
  kind: infoModalKind
}

const InfoModal = ({ kind, onClose }: Props) => {
  const Content = (
    <Box sx={{ textAlign: 'center' }}>
      <img src={ModalInfoIcon} alt="ModalInfoIcon" width={250} height={250} />
      <Typography variant="h5" mb="16px">
        {kind}
      </Typography>
      {Info[kind].infos.map((info, i) => (
        <Typography variant="body2" align="center" key={i}>
          {info}
        </Typography>
      ))}
      <Box
        sx={{ display: 'flex', width: '100%', gap: '16px', marginTop: '40px' }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            window.open(Info[kind].url, '_blank')
          }}
          fullWidth
        >
          Learn more
        </Button>
        <Button variant="contained" onClick={onClose} fullWidth>
          Ok, got it!
        </Button>
      </Box>
    </Box>
  )

  return <CoreModal open content={Content} onClose={onClose} />
}

export default InfoModal

const Info: Record<infoModalKind, { infos: string[]; url: string }> = {
  'Borrowing power': {
    infos: [
      'It determines amount of assets you can borrow from your collateral. It, however, doesn’t take borrowing factors into account.',
      'To increase your borrowing power, use an asset you have supplied as collateral.',
    ],
    url: GitBook.genesisPool,
  },
  'Collateral factor': {
    infos: [
      'The factor determines  the borrowing power you will get from supplying an asset.',
      'For example, if you supplies $100 worth of collateral, and the collateral factor of that asset is 75%, then your borrowing power will be at most $75 worth of other assets.',
    ],
    url: GitBook.genesisPool,
  },
  'Borrowing factor': {
    infos: [
      'The factor determines what proportion of a specific asset can be borrowed. The riskier the asset is, the higher its borrowing factor is, because we are more lenient to let users borrow out riskier asset compared to less risky asset.',
    ],
    url: GitBook.genesisPool,
  },
  'Borrow limit': {
    infos: [
      'Borrow limit determines the maximum amount of an asset you can borrow, given the borrowing power and the borrowing factor of that asset.',
    ],
    url: GitBook.genesisPool,
  },
  'Utilization rate': {
    infos: [
      'Utilization rate determines the percentage of total liquidity supplied of an asset that is being borrowed.',
    ],
    url: GitBook.genesisPool,
  },
  'Risk Ratio': {
    infos: [
      'Risk ratio indicates how much of  your borrowing power is consumed by your debt and its borrowing factors. Your position will be liquidated once the risk ratio reaches 100%.',
    ],
    url: GitBook.genesisPool,
  },
  'Boost borrowing power with collateral': {
    infos: [
      'By turning on the toggle, you allow an asset to be used as collateral. Hence, your borrowing power will increase by the amount of that asset you have supplied multiplied with its collateral factor.',
    ],
    url: GitBook.genesisPool,
  },
  'Liquidation incentive': {
    infos: [
      'An additional percentage of collateral given to liquidators to perform liquidation.',
    ],
    url: GitBook.genesisPool,
  },
  'Reserve factor': {
    infos: [
      'Percentage withholded as an insurance fund for the pool to repay underwater liquidation debt.',
    ],
    url: GitBook.genesisPool,
  },
}
